/**
 * ReminderEditorDialog.jsx
 *
 * Create / edit / preview a Scheduled Reminder.
 *
 * Save flow (orchestrated client-side, matching existing doc_alert pattern):
 *
 *   CREATE:
 *     1. insertTemplate({ description, type: 'reminder', brandLabel, isSystem: 0 })
 *        → { id: templateId }
 *     2. insertRule({ type: 'scheduled', template_id: templateId, is_active, document_id: null })
 *        → { id: ruleId }
 *     3. insertCalendarEvent(ruleId, { rrule, starts_at, recurrence_end })
 *        → { id: calendarEventId }
 *     4. upsertTemplateTranslation(templateId, 'en', { subject, body_html, body_text })  (if filled)
 *     5. upsertTemplateTranslation(templateId, 'es', { ... })                             (if filled)
 *
 *   UPDATE:
 *     1. updateTemplate(templateId, { description })
 *     2. updateRule(ruleId, { is_active })
 *     3. updateCalendarEvent(calendarEventId, { rrule, starts_at, recurrence_end })
 *     4. upsertTemplateTranslation(templateId, locale, { ... })  for each filled locale
 *
 *   DELETE (handled in ScheduledReminders):
 *     1. deleteRule(ruleId)             — soft-deletes notification_rules
 *     2. deleteCalendarEvent(ceId)      — hard-deletes calendar_events
 *     (translations + template left intact for audit trail)
 *
 * Props:
 *   open      — bool
 *   onClose   — () => void
 *   onSaved   — ({ rule_id, template_id, description }) => void
 *   reminder  — null (create) | { rule_id, template_id, calendar_event_id, description, is_active, rrule, starts_at, recurrence_end }
 *   readOnly  — bool (preview mode)
 */

import React, { useState, useRef, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  MenuItem,
  Grid
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { useTranslation } from 'react-i18next'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import {
  insertTemplate,
  updateTemplate,
  insertRule,
  updateRule,
  insertCalendarEvent,
  updateCalendarEvent,
  upsertTemplateTranslation,
  getReminderById
} from '../../../services/notificationRules'
import CPTip from '../../Utils/CPTip'

// ─── Quill ────────────────────────────────────────────────────────────────────
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
}
const QUILL_MODULES_READONLY = { toolbar: false }

const AVAILABLE_VARS = [
  '{{recipient_email}}',
  '{{recipient_name}}'
]
const EMPTY_TRANSLATION = { subject: '', body_html: '', body_text: '' }

// ─── RRule helpers ────────────────────────────────────────────────────────────
const DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']
const FREQ_OPTIONS = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY']

function buildRRule(freq, startsAt, recurrenceEnd) {
  if (!freq || !startsAt) return ''
  const d = new Date(startsAt)
  const day = DAY_CODES[d.getDay()]
  const dayOfMonth = d.getDate()
  const month = d.getMonth() + 1

  let rule = `FREQ=${freq}`
  if (freq === 'WEEKLY') rule += `;BYDAY=${day}`
  else if (freq === 'MONTHLY') rule += `;BYMONTHDAY=${dayOfMonth}`
  else if (freq === 'YEARLY')
    rule += `;BYMONTH=${month};BYMONTHDAY=${dayOfMonth}`

  if (recurrenceEnd) {
    const until = new Date(recurrenceEnd)
    until.setUTCHours(23, 59, 59, 0)
    rule += `;UNTIL=${until.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`
  }
  return rule
}

function extractFreq(rrule) {
  if (!rrule) return ''
  const m = rrule.match(/FREQ=([A-Z]+)/)
  return m ? m[1] : ''
}

function freqLabel(freq, startsAt) {
  if (!startsAt) return freq
  const d = new Date(startsAt)
  const dayName = d.toLocaleDateString('en-US', { weekday: 'long' })
  const dayOfMonth = d.getDate()
  const monthName = d.toLocaleDateString('en-US', { month: 'long' })
  switch (freq) {
    case 'DAILY':
      return 'Daily'
    case 'WEEKLY':
      return `Weekly on ${dayName}`
    case 'MONTHLY':
      return `Monthly on the ${dayOfMonth}`
    case 'YEARLY':
      return `Annually on ${monthName} ${dayOfMonth}`
    default:
      return freq
  }
}

// ─── ScheduleForm ─────────────────────────────────────────────────────────────
// scheduleType: 'fixed_date' | 'recurring'
// fixed_date  → only starts_at, rrule is null
// recurring   → starts_at + recurrence_end + rrule (FREQ=...)
function ScheduleForm({
  scheduleType,
  onScheduleTypeChange,
  rrule,
  startsAt,
  recurrenceEnd,
  onChange,
  readOnly,
  t
}) {
  const freq = extractFreq(rrule)

  const handleScheduleTypeChange = (newType) => {
    onScheduleTypeChange(newType)
    // Clear rrule and recurrence_end when switching to fixed_date
    if (newType === 'fixed_date') {
      onChange({ rrule: '', starts_at: startsAt, recurrence_end: null })
    }
  }

  const handleStartsAtChange = (newVal) => {
    const date = newVal?.$d ?? newVal
    const newRRule =
      scheduleType === 'recurring' ? buildRRule(freq, date, recurrenceEnd) : ''
    onChange({
      rrule: newRRule,
      starts_at: date,
      recurrence_end: recurrenceEnd
    })
  }

  const handleRecurrenceEndChange = (newVal) => {
    const date = newVal?.$d ?? newVal
    onChange({
      rrule: buildRRule(freq, startsAt, date),
      starts_at: startsAt,
      recurrence_end: date
    })
  }

  const handleFreqChange = (newFreq) => {
    onChange({
      rrule: buildRRule(newFreq, startsAt, recurrenceEnd),
      starts_at: startsAt,
      recurrence_end: recurrenceEnd
    })
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid container spacing={2} sx={{ mt: 0 }}>
        {/* Type selector */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            size='small'
            label={t('Schedule type')}
            value={scheduleType}
            onChange={(e) => handleScheduleTypeChange(e.target.value)}
            InputProps={{ readOnly }}
          >
            <MenuItem value='fixed_date'>{t('On a specific date')}</MenuItem>
            <MenuItem value='recurring'>{t('Recurring')}</MenuItem>
          </TextField>
        </Grid>

        {/* fixed_date — single date picker */}
        {scheduleType === 'fixed_date' && (
          <Grid item xs={12}>
            <DesktopDatePicker
              label={t('Date')}
              value={startsAt || null}
              onChange={handleStartsAtChange}
              disabled={readOnly}
              renderInput={(params) => (
                <TextField {...params} fullWidth size='small' />
              )}
            />
          </Grid>
        )}

        {/* recurring — start + end + pattern */}
        {scheduleType === 'recurring' && (
          <>
            <Grid item xs={12} sm={6}>
              <DesktopDatePicker
                label={t('Start date')}
                value={startsAt || null}
                onChange={handleStartsAtChange}
                disabled={readOnly}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size='small' />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DesktopDatePicker
                label={t('End date (optional)')}
                value={recurrenceEnd || null}
                onChange={handleRecurrenceEndChange}
                minDate={startsAt || undefined}
                disabled={readOnly}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size='small' />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size='small'
                label={t('Recurrence pattern')}
                value={freq}
                onChange={(e) => handleFreqChange(e.target.value)}
                InputProps={{ readOnly }}
              >
                {FREQ_OPTIONS.map((f) => (
                  <MenuItem key={f} value={f}>
                    {freqLabel(f, startsAt)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {rrule && (
              <Grid item xs={12}>
                <Typography variant='caption' color='text.secondary'>
                  {t('Rule')}: <code>{rrule}</code>
                </Typography>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </LocalizationProvider>
  )
}

// ─── TabPanel ─────────────────────────────────────────────────────────────────
function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null
}

// ─── TranslationForm ──────────────────────────────────────────────────────────
function TranslationForm({ value, onChange, quillRef, readOnly, t }) {
  const insertVar = (variable) => {
    const editor = quillRef.current?.getEditor()
    if (editor) {
      const range = editor.getSelection(true)
      editor.insertText(range.index, variable)
    }
  }

  return (
    <Box>
      <TextField
        fullWidth
        size='small'
        label={t('Subject')}
        value={value.subject}
        onChange={(e) =>
          !readOnly && onChange({ ...value, subject: e.target.value })
        }
        sx={{ mb: 2 }}
        placeholder='e.g. {{recipient_name}} — monthly update'
        InputProps={{ readOnly }}
      />
      {!readOnly && (
        <Box sx={{ mb: 1 }}>
          <Typography variant='caption' color='text.secondary' sx={{ mr: 1 }}>
            {t('Insert variable')}:
          </Typography>
          {AVAILABLE_VARS.map((v) => (
            <Chip
              key={v}
              label={v}
              size='small'
              sx={{
                mr: 0.5,
                mb: 0.5,
                cursor: 'pointer',
                fontFamily: 'monospace'
              }}
              onClick={() => insertVar(v)}
            />
          ))}
        </Box>
      )}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant='caption'
          color='text.secondary'
          display='block'
          sx={{ mb: 0.5 }}
        >
          {t('Email body')}
        </Typography>
        <ReactQuill
          ref={quillRef}
          theme='snow'
          value={value.body_html}
          onChange={(html) =>
            !readOnly && onChange({ ...value, body_html: html })
          }
          modules={readOnly ? QUILL_MODULES_READONLY : QUILL_MODULES}
          readOnly={readOnly}
          style={{
            minHeight: 180,
            backgroundColor: readOnly ? 'transparent' : undefined
          }}
        />
      </Box>
      <TextField
        fullWidth
        size='small'
        multiline
        minRows={3}
        label={t('Plaintext fallback')}
        value={value.body_text}
        onChange={(e) =>
          !readOnly && onChange({ ...value, body_text: e.target.value })
        }
        helperText={
          !readOnly
            ? t('Shown in email clients that do not support HTML')
            : undefined
        }
        InputProps={{ readOnly }}
      />
    </Box>
  )
}

// ─── ReminderEditorDialog ─────────────────────────────────────────────────────
export default function ReminderEditorDialog({
  open,
  onClose,
  onSaved,
  reminder,
  readOnly = false
}) {
  const { t } = useTranslation()
  const quillEnRef = useRef(null)
  const quillEsRef = useRef(null)

  const isCreate = !reminder

  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [scheduleType, setScheduleType] = useState('fixed_date') // 'fixed_date' | 'recurring'
  const [rrule, setRrule] = useState('')
  const [startsAt, setStartsAt] = useState(null)
  const [recurrenceEnd, setRecurrenceEnd] = useState(null)
  const [en, setEn] = useState(EMPTY_TRANSLATION)
  const [es, setEs] = useState(EMPTY_TRANSLATION)

  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // ── Load existing ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !reminder?.rule_id) return
    setLoading(true)
    getReminderById(reminder.rule_id)
      .then((res) => {
        setDescription(res.description || '')
        setIsActive(res.is_active !== false)
        // Derive scheduleType from whether an rrule exists
        setScheduleType(res.rrule ? 'recurring' : 'fixed_date')
        setRrule(res.rrule || '')
        setStartsAt(res.starts_at || null)
        setRecurrenceEnd(res.recurrence_end || null)

        const enRow = (res.translations || []).find((tr) => tr.locale === 'en')
        const esRow = (res.translations || []).find((tr) => tr.locale === 'es')
        setEn(
          enRow
            ? {
                subject: enRow.subject,
                body_html: enRow.body_html,
                body_text: enRow.body_text
              }
            : EMPTY_TRANSLATION
        )
        setEs(
          esRow
            ? {
                subject: esRow.subject,
                body_html: esRow.body_html,
                body_text: esRow.body_text
              }
            : EMPTY_TRANSLATION
        )
      })
      .catch(() => setError(t('Failed to load reminder')))
      .finally(() => setLoading(false))
  }, [open, reminder?.rule_id])

  // ── Reset for create ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || reminder?.rule_id) return
    setDescription('')
    setIsActive(true)
    setScheduleType('fixed_date')
    setRrule('')
    setStartsAt(null)
    setRecurrenceEnd(null)
    setEn(EMPTY_TRANSLATION)
    setEs(EMPTY_TRANSLATION)
    setTab(0)
    setError('')
  }, [open, reminder?.rule_id])

  const handleScheduleChange = ({
    rrule: r,
    starts_at: s,
    recurrence_end: e
  }) => {
    setRrule(r)
    setStartsAt(s)
    setRecurrenceEnd(e)
  }

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = () => {
    if (!description.trim()) return t('Please enter a reminder name')
    if (!startsAt) return t('Please select a date')
    if (scheduleType === 'recurring' && !rrule)
      return t('Please select a recurrence pattern')
    const enFilled = en.subject.trim() || en.body_html.trim()
    const esFilled = es.subject.trim() || es.body_html.trim()
    if (!enFilled && !esFilled) return t('Please fill in at least one language')
    if (enFilled && !en.subject.trim()) return t('English subject is required')
    if (enFilled && !en.body_html.trim())
      return t('English email body is required')
    if (enFilled && !en.body_text.trim())
      return t('English plaintext fallback is required')
    if (esFilled && !es.subject.trim()) return t('Spanish subject is required')
    if (esFilled && !es.body_html.trim())
      return t('Spanish email body is required')
    if (esFilled && !es.body_text.trim())
      return t('Spanish plaintext fallback is required')
    return null
  }

  // ── Save — orchestrated multi-step ─────────────────────────────────────────
  const handleSave = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    setError('')
    setSaving(true)

    try {
      let templateId, ruleId

      if (isCreate) {
        // Step 1 — create template (type='reminder')
        const tmplRes = await insertTemplate({
          description,
          isSystem: 0,
          type: 'reminder',
          brandLabel: window.$brand
        })
        templateId = tmplRes.id

        // Step 2 — create rule (type='scheduled', no document)
        const ruleRes = await insertRule({
          type: 'scheduled',
          template_id: templateId,
          document_id: null,
          is_active: isActive
        })
        ruleId = ruleRes.id

        // Step 3 — create calendar_event
        await insertCalendarEvent(ruleId, {
          rrule: rrule || null,
          title: description,
          description: description,
          starts_at: startsAt,
          recurrence_end: recurrenceEnd
        })
      } else {
        templateId = reminder.template_id
        ruleId = reminder.rule_id

        // Step 1 — update template description
        await updateTemplate(templateId, { description })

        // Step 2 — update rule is_active
        await updateRule(ruleId, { is_active: isActive })

        // Step 3 — update calendar_event
        await updateCalendarEvent(reminder.calendar_event_id, {
          rrule: rrule || null,
          title: description,
          description: description,
          starts_at: startsAt,
          recurrence_end: recurrenceEnd
        })
      }

      // Steps 4+5 — upsert translations (reuses existing function, same table)
      const translationSaves = []
      if (en.subject.trim())
        translationSaves.push(upsertTemplateTranslation(templateId, 'en', en))
      if (es.subject.trim())
        translationSaves.push(upsertTemplateTranslation(templateId, 'es', es))
      await Promise.all(translationSaves)

      onSaved({ rule_id: ruleId, template_id: templateId, description })
      handleClose()
    } catch {
      setError(t('An error occurred. Please try again.'))
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    setDescription('')
    setIsActive(true)
    setScheduleType('fixed_date')
    setRrule('')
    setStartsAt(null)
    setRecurrenceEnd(null)
    setEn(EMPTY_TRANSLATION)
    setEs(EMPTY_TRANSLATION)
    setTab(0)
    setError('')
    onClose()
  }

  const enHasContent = en.subject.trim() || en.body_html.trim()
  const esHasContent = es.subject.trim() || es.body_html.trim()

  const dialogTitle = readOnly
    ? t('Reminder')
    : isCreate
      ? t('New scheduled reminder')
      : t('Edit scheduled reminder')

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {!readOnly && (
              <CPTip
                text={t(
                  'Reminders are sent to all portal users on the configured schedule. No recipient setup needed.'
                )}
              />
            )}

            {/* Name + active toggle */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                mt: 2,
                mb: 2
              }}
            >
              <TextField
                fullWidth
                size='small'
                label={t('Reminder name')}
                value={description}
                onChange={(e) => !readOnly && setDescription(e.target.value)}
                InputProps={{ readOnly }}
              />
              {!readOnly && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      size='small'
                    />
                  }
                  label={
                    <Typography variant='caption'>{t('Active')}</Typography>
                  }
                  sx={{ whiteSpace: 'nowrap', mr: 0 }}
                />
              )}
            </Box>

            {/* Schedule → calendar_events: rrule, starts_at, recurrence_end */}
            <Typography
              variant='caption'
              color='text.secondary'
              fontWeight={600}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              {t('Schedule')}
            </Typography>

            <ScheduleForm
              scheduleType={scheduleType}
              onScheduleTypeChange={setScheduleType}
              rrule={rrule}
              startsAt={startsAt}
              recurrenceEnd={recurrenceEnd}
              onChange={handleScheduleChange}
              readOnly={readOnly}
              t={t}
            />

            <Divider sx={{ my: 2.5 }} />

            {/* Email content → notification_template_translations */}
            <Typography
              variant='caption'
              color='text.secondary'
              fontWeight={600}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              {t('Email content')}
            </Typography>

            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', mt: 1 }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {t('English')}
                    {enHasContent && (
                      <Chip
                        label='EN'
                        size='small'
                        color='primary'
                        sx={{ height: 16, fontSize: 10 }}
                      />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {t('Spanish')}
                    {esHasContent && (
                      <Chip
                        label='ES'
                        size='small'
                        color='primary'
                        sx={{ height: 16, fontSize: 10 }}
                      />
                    )}
                  </Box>
                }
              />
            </Tabs>

            <TabPanel value={tab} index={0}>
              <TranslationForm
                value={en}
                onChange={setEn}
                quillRef={quillEnRef}
                readOnly={readOnly}
                t={t}
              />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <TranslationForm
                value={es}
                onChange={setEs}
                quillRef={quillEsRef}
                readOnly={readOnly}
                t={t}
              />
            </TabPanel>

            {error && (
              <Typography
                variant='caption'
                color='error'
                sx={{ mt: 1, display: 'block' }}
              >
                {error}
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {readOnly ? t('Close') : t('Cancel')}
        </Button>
        {!readOnly && (
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : t('Save reminder')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
